import { useState, useEffect, useRef } from 'preact/hooks';

/**
 * Hook to lazy-load location detail (description) from the REST API.
 * Caches results to avoid re-fetching when the same location is reopened.
 *
 * @param  id The location post ID, or null when no dialog is open.
 * @return {Object} Object containing detail data, loading state, and error state.
 */
export function useLocationDetail( id: number | null ) {
	const [ detail, setDetail ] = useState< LocationDetail | null >( null );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ error, setError ] = useState< string | null >( null );
	const cache = useRef< Map< number, LocationDetail > >( new Map() );

	useEffect( () => {
		// Reset when no location is selected.
		if ( id === null ) {
			setDetail( null );
			setIsLoading( false );
			setError( null );
			return;
		}

		// Return cached result if available.
		const cached = cache.current.get( id );
		if ( cached ) {
			setDetail( cached );
			setIsLoading( false );
			setError( null );
			return;
		}

		const { detailUrl } = window.strMapsSettings || {};

		if ( ! detailUrl ) {
			setError( 'Map settings not found.' );
			return;
		}

		setIsLoading( true );
		setError( null );
		setDetail( null );

		const controller = new AbortController();

		const url = new URL( detailUrl, window.location.origin );
		url.searchParams.set( 'id', String( id ) );

		fetch( url.toString(), {
			signal: controller.signal,
		} )
			.then( ( response ) => {
				if ( ! response.ok ) {
					throw new Error( `Failed to fetch location detail: ${ response.status }` );
				}
				return response.json() as Promise< { id: number; data: LocationDetail } >;
			} )
			.then( ( result ) => {
				const locationDetail = result.data;
				cache.current.set( id, locationDetail );
				setDetail( locationDetail );
				setIsLoading( false );
			} )
			.catch( ( err ) => {
				if ( err.name !== 'AbortError' ) {
					// eslint-disable-next-line no-console
					console.error( 'Error fetching location detail:', err );
					setError( err.message );
					setIsLoading( false );
				}
			} );

		// Cleanup: abort fetch on unmount or id change.
		return () => {
			controller.abort();
		};
	}, [ id ] );

	return { detail, isLoading, error };
}
